/**
 * Storage utility for household view persistence
 *
 * This provides tab-specific storage for household view state using sessionStorage,
 * which is isolated to the current tab/window. This allows different tabs
 * to have different view settings.
 */

import { householdsViewStorageKey } from "@src/constants/localStorageKeys";
import { ClientType } from "@src/multiCustodian/components/Advisor/Households/constants";

// Define key for tab-specific storage
const tabKey = `${householdsViewStorageKey}.tab`;

// Household view parameters interface
interface HouseholdViewParams {
  currentView: ClientType;
}

/**
 * Saves household view to tab-specific storage (sessionStorage)
 */
export function saveViewToTab(view: ClientType): void {
  try {
    const viewParams: HouseholdViewParams = {
      currentView: view,
    };

    sessionStorage.setItem(tabKey, JSON.stringify(viewParams));
  } catch (error) {
    console.error("Error saving household view to session storage:", error);
  }
}

/**
 * Gets household view from tab-specific storage (sessionStorage)
 */
export function getViewFromTab(defaultView: ClientType): ClientType {
  try {
    const savedView = sessionStorage.getItem(tabKey);

    if (savedView) {
      const parsedView = JSON.parse(savedView) as HouseholdViewParams;

      // Basic validation - ensure it's a valid ClientType
      if (
        parsedView &&
        parsedView.currentView &&
        Object.values(ClientType).includes(parsedView.currentView)
      ) {
        return parsedView.currentView;
      }
    }
  } catch (error) {
    console.error("Error loading household view from session storage:", error);
  }

  return defaultView;
}
